/**
 * Coarse light field — docs/01-architecture.md §6.3.
 *
 * §6.3 asks for a ¼-resolution field of emissive cells "propagated with a small
 * number of jump-flood-ish blur plies that respect terrain opacity". This is
 * that field, computed on the CPU at ⅛ resolution rather than on the GPU at ¼.
 * Three requirements the GPU sketch could not have met:
 *
 *   - **It has to be budgeted like a simulation system.** §9 requires a
 *     deterministic work quota, a profiler bucket and a per-system budget
 *     assertion in `tests/sim.test.ts`. A framebuffer pass is none of those.
 *   - **It has to respect chunk sleeping.** A full-viewport GPU pass pays for
 *     every chunk every frame whether anything changed or not, which is exactly
 *     the scheduler defeat this field was not allowed to be.
 *   - **Simulation may need to read it.** Presentation reads simulation and
 *     never the reverse (§2). Light that lives only in a framebuffer can never
 *     answer "is the player standing in the dark", and §6.3's whole claim is
 *     that darkness is a mechanic.
 *
 * ⅛ rather than ¼ because the field's job is the *falloff*, not the source.
 * Fire, lava and charged matter already glow per-cell in the terrain shader;
 * what was missing is that they lit nothing else. A 16×16 tile grid per chunk
 * is 256 tiles where ¼ resolution is 1024, the shader samples it bilinearly so
 * the gradient is smooth either way, and propagation costs a quarter as much.
 *
 * ## Shape
 *
 * Two passes, both quota-bounded, both resumable by chunk identity:
 *
 *   1. **Gather** turns cells into tiles: per tile, the summed emission of its
 *      64 cells and their mean opacity. Driven by the chunk's existing movement
 *      and thermal dirty rects — the two rects that between them cover every
 *      way a cell's emission or opacity can change — so a chunk whose cells did
 *      not change is not re-read. This is what keeps sleeping chunks free.
 *   2. **Relax** propagates one ply per tick over the tiles:
 *      `light = max(emit, maxNeighbourLight × FALLOFF × transmit)`.
 *      Max-propagation rather than a blur because a blur of a bright source
 *      *dims* it, and because max-propagation has a fixed point that light
 *      converges to and stays at instead of oscillating.
 *
 * Relax reads and writes the same buffer (Gauss-Seidel), which converges to the
 * same fixed point as a double-buffered ply in a fraction of the ticks. The
 * price is that a single ply carries light further along the scan direction
 * than against it; the scan direction flips on `world.tick & 1`, so the
 * asymmetry cancels within one frame and never lands on the fixed point. It is
 * driven by the tick counter and not by a clock, per §9.
 *
 * ## Why this does not wake chunks
 *
 * Nothing here calls `touch`, `touchTemp` or `set`. The field only ever reads
 * cells. A sleeping chunk next to a fire is lit — its `LightChunk` is relaxed
 * every tick — and stays asleep, because relax operates on the tile grid and
 * never on cells. `tests/sim.test.ts` asserts this directly.
 */

import { profiler } from '../core/profiler';
import { Flag, M, materialCount } from './materials';
import { CHUNK_MASK, CHUNK_SHIFT, CellWorld, Tier, type Chunk } from './world';

/** Cells per light tile, per axis. 8 → ⅛ resolution. */
export const LIGHT_SHIFT = 3;
export const LIGHT_TILE = 1 << LIGHT_SHIFT;
export const LIGHT_AXIS_SHIFT = CHUNK_SHIFT - LIGHT_SHIFT;
export const LIGHT_PER_AXIS = 1 << LIGHT_AXIS_SHIFT; // 16
export const LIGHT_TILES = LIGHT_PER_AXIS * LIGHT_PER_AXIS; // 256

const TILE_MASK = LIGHT_PER_AXIS - 1;
const CELLS_PER_TILE = LIGHT_TILE * LIGHT_TILE;

/**
 * Light retained per tile crossed, before opacity. 0.72 over an 8-cell tile is
 * roughly a 40-cell useful radius for a saturated source, which is the scale a
 * 480×270 viewport wants: a fire lights its room, not the whole biome.
 */
const FALLOFF = 0.72;

/**
 * How much of a tile's opacity is subtracted from what it passes on.
 *
 * Deliberately not 1. A fully opaque tile still transmits 0.2, because the tile
 * containing a wall's lit *face* is mostly wall, and a hard cutoff renders the
 * face of a rock next to a bonfire black. Two tiles deep the product is 0.04
 * and the interior is dark, which is the part that has to be true.
 */
const OCCLUDE = 0.8;

/** Emission per source cell. ~7 saturated cells fill a tile. */
const PER_CELL_GAIN = 0.14;

/** A burning cell emits regardless of its material's own `emissive`. */
const BURN_EMIT = 0.55;
const FIRE_R = 1.0;
const FIRE_G = 0.55;
const FIRE_B = 0.2;

/** `Flag.LIT` is the injection point for abilities and props (§3.2 bit 8). */
const LIT_EMIT = 0.8;

/** Below this a tile is treated as dark — retirement and spill both use it. */
const EPS = 1 / 512;

/**
 * Consecutive dark ticks before a chunk's tiles are released.
 *
 * Hysteresis, not politeness. A chunk right on the edge of the lit set can sit
 * exactly where the light arriving from its neighbour rounds to zero: retire it
 * the instant it reads dark and the neighbour's `spill` re-admits it on the
 * same tick, at a fresh 16384-cell gather, forever. Eight matches the chunk
 * scheduler's own `SLEEP_AFTER` for the same reason it was chosen there.
 */
const RETIRE_AFTER = 8;

/**
 * Cost of one tile relaxation in cell-equivalent work units.
 *
 * The quota below is denominated in **cells**, not chunks and not tiles, for
 * the reason `thermal.ts` documents at length: a chunk-denominated unit spanned
 * a ~2000× cost range and blew a 1 ms line item by 21×. A tile relaxation is
 * not a cell, so it is priced against one — four neighbour maxima on three
 * channels plus the border scan `spill` needs, against one material lookup and
 * two accumulates.
 *
 * The check that the weight is right is that end-to-end cost per unit stays
 * flat across scenes with very different gather/relax mixes: 11–15 ns on an
 * 8-chunk oil flood (gather-heavy), 12–14 ns on a 21-chunk burning screen
 * (relax-heavy). A mispriced unit would show up as those diverging, which is
 * exactly the signal the chunk quota could not produce.
 */
const RELAX_COST = 2;

/**
 * Fraction of the quota gather may take before relax gets the rest.
 *
 * Without a split, a camera cut queues a dozen full-chunk gathers and starves
 * propagation for as many ticks as they take — the field would hold sources it
 * never spread. The minority share goes to gather because a late gather is a
 * fire one tile out of date, while a late relax is light that has not arrived:
 * 60% of 16000 units is 9600, which is 18 chunks of propagation a tick against
 * the 7–21 chunk lit sets the stress scenes produce.
 */
const GATHER_SHARE = 0.4;

/**
 * Work units charged for admitting one cold chunk.
 *
 * Admission is not free and used not to be counted at all: a `LightChunk` is
 * 1792 floats of fresh allocation plus a 256-element `transmit` fill, and it
 * makes the relax order dirty. Measured at 1.3–2.0 µs per admission over runs
 * that took on 20–63 chunks at once (Node 22, darwin/arm64), against the 11–15
 * ns a work unit costs — so one admission is ~100–160 units, and 128 prices it
 * without pretending to more precision than the measurement has.
 */
const ADMIT_COST = 128;

function chunkKey(cx: number, cy: number): number {
  return ((cx + 32768) << 16) | (cy + 32768);
}

/**
 * One chunk's tile grid.
 *
 * Held here rather than on `Chunk` so the cost is paid only by chunks that are
 * actually near light. A dark cave two screens from the nearest fire allocates
 * nothing.
 */
class LightChunk {
  readonly cx: number;
  readonly cy: number;
  chunk: Chunk;

  /** Source radiance per tile, RGB interleaved. Written by gather. */
  readonly emit = new Float32Array(LIGHT_TILES * 3);
  /** Propagated radiance per tile, RGB interleaved. Written by relax. */
  readonly light = new Float32Array(LIGHT_TILES * 3);
  /** 1 − OCCLUDE × mean opacity. Written by gather. */
  readonly transmit = new Float32Array(LIGHT_TILES);

  /** Tiles awaiting a gather. minTx > maxTx means none. */
  pMinTx = 0;
  pMinTy = 0;
  pMaxTx = TILE_MASK;
  pMaxTy = TILE_MASK;

  hasEmit = false;
  maxLight = 0;

  /**
   * Set once a ply changed nothing here, cleared when this chunk's cells or a
   * neighbour's light change.
   *
   * Without it a torch-lit room costs a full ply of every lit chunk forever,
   * which is the same "pay for it whether or not anything changed" shape the
   * dirty rects exist to kill one layer down. A converged field is the common
   * case — most of the time the fire is not spreading, it is just burning.
   */
  settled = false;

  /** Consecutive relaxes that found no source and no light. See RETIRE_AFTER. */
  darkTicks = 0;

  constructor(chunk: Chunk) {
    this.chunk = chunk;
    this.cx = chunk.cx;
    this.cy = chunk.cy;
    // Ungathered tiles must not block light, or a chunk that light has just
    // spilled into would be a wall until its gather lands.
    this.transmit.fill(1);
  }

  get pending(): boolean {
    return this.pMaxTx >= this.pMinTx;
  }

  clearPending(): void {
    this.pMinTx = 1;
    this.pMaxTx = 0;
  }

  fullPending(): void {
    this.pMinTx = 0;
    this.pMinTy = 0;
    this.pMaxTx = TILE_MASK;
    this.pMaxTy = TILE_MASK;
  }

  /** Union a cell-space rect in, snapped outward to whole tiles. */
  addCellRect(minX: number, minY: number, maxX: number, maxY: number): void {
    const tx0 = minX >> LIGHT_SHIFT;
    const ty0 = minY >> LIGHT_SHIFT;
    const tx1 = maxX >> LIGHT_SHIFT;
    const ty1 = maxY >> LIGHT_SHIFT;
    if (!this.pending) {
      this.pMinTx = tx0;
      this.pMinTy = ty0;
      this.pMaxTx = tx1;
      this.pMaxTy = ty1;
      return;
    }
    if (tx0 < this.pMinTx) this.pMinTx = tx0;
    if (ty0 < this.pMinTy) this.pMinTy = ty0;
    if (tx1 > this.pMaxTx) this.pMaxTx = tx1;
    if (ty1 > this.pMaxTy) this.pMaxTy = ty1;
  }
}

export class LightField {
  private world: CellWorld;
  private map = new Map<number, LightChunk>();

  /**
   * Relax order, sorted by (cy, cx).
   *
   * Not the map's insertion order: that follows chunk residency, so a world
   * reached by walking left and one reached by walking right would relax in
   * different orders and — because relax is Gauss-Seidel — settle on the same
   * fixed point by a different path. Sorting makes the intermediate states
   * reproducible too, which is what the §8 determinism gate actually asserts.
   */
  private order: LightChunk[] = [];
  private orderDirty = false;
  private retire: LightChunk[] = [];

  /** Per-material premultiplied emission, built once from the palette. */
  private srcR = new Float32Array(0);
  private srcG = new Float32Array(0);
  private srcB = new Float32Array(0);
  private tablesFor = -1;

  /**
   * Deterministic work quota per tick, in **cell-equivalent units** — a
   * gathered cell is 1, a relaxed tile is `RELAX_COST`.
   *
   * Calibrated against the 0.4 ms budget added to docs/01 §9: a unit measures
   * 11–15 ns across the stress scenes (Node 22, darwin/arm64), so a saturated
   * 16000-unit tick is 0.18–0.24 ms.
   *
   * This quota genuinely binds, which is the property `thermal.ts`'s did not
   * have before it was fixed and which §9 states outright — "a quota that has
   * never been reached has never been tested". Uncapped, a screen of burning
   * oil demands 30k units a tick in steady state and 127k on the tick a cold
   * field admits every burning chunk at once. The cap turns that into a flat
   * line and costs a few ticks of fade-in instead of a dropped frame.
   */
  maxWorkPerTick = 16000;

  /**
   * Deterministic admission quota: cold chunks the field may take on per tick.
   *
   * `maxWorkPerTick` bounded gather and relax and left admission unbounded, so
   * the first tick after a camera move into unlit territory took on every cold
   * chunk at once — 63 of them after #31 tripled the emitter count. The mean was
   * untouched (admission is cheap per chunk), which is exactly why the mean-based
   * budget fixtures could not see it; it is a p99 problem, and §9's rule is that
   * *every* scheduling decision is a deterministic quota, which admission was not.
   *
   * 8 is picked against a fast pan rather than a slow walk. A pan brings at most
   * one chunk column — five chunks plus the WARM rings — into range every ~19
   * ticks even at the fastest camera speed the game allows, so 8 never throttles
   * a pan at all. What it stages is the hard cut, where 63 chunks arrive on one
   * tick and the field then needs ~160 ticks of gather to light them anyway: the
   * eight ticks admission now takes are inside the fade-in that was already
   * happening, so nothing pops that did not pop before.
   */
  maxAdmitPerTick = 8;

  /** Units spent this tick. Telemetry, and the subject of the quota test. */
  workThisTick = 0;

  /** Cold chunks admitted this tick, against `maxAdmitPerTick`. */
  admitsThisTick = 0;

  /**
   * Chunks waiting to be admitted, as flat (cx, cy) pairs, oldest first.
   *
   * A queue rather than "re-offer it next tick": `sync` only ever sees chunks in
   * `activeChunks`, and a freshly generated chunk full of static glow veins goes
   * quiet and drops out of that list after eight ticks. A deferred admission that
   * relied on being re-offered would therefore be dropped for good on exactly the
   * scene this quota exists for. It also decouples `spill` from the settling
   * logic: a chunk that settles the tick after it spilled would otherwise never
   * get another chance to admit the neighbour its light is crossing into.
   */
  private admitQueue: number[] = [];
  private admitQueued = new Set<number>();

  /** Resume points, by chunk identity — `order` is rebuilt as chunks come and go. */
  private gatherCx = 0;
  private gatherCy = 0;
  private gatherTy = -1;
  private gatherValid = false;
  private relaxCx = 0;
  private relaxCy = 0;
  private relaxValid = false;

  constructor(world: CellWorld) {
    this.world = world;
  }

  /** Chunks currently carrying light. Telemetry and tests. */
  get litChunks(): number {
    return this.order.length;
  }

  step(): void {
    profiler.begin('light');
    this.workThisTick = 0;
    this.admitsThisTick = 0;
    this.ensureTables();
    // `sync` only queues. Admission runs before gather so a chunk queued this
    // tick is still lit this tick when the quota has room, and charges itself to
    // the same `workThisTick` gather and relax then divide up — one tick, one
    // number, which is what makes the budget assertable in units at all.
    this.sync();
    this.drainAdmissions();
    this.runGather();
    this.runRelax();
    profiler.setCount('lightWork', this.workThisTick);
    profiler.setCount('lightChunks', this.order.length);
    profiler.setCount('lightAdmits', this.admitsThisTick);
    profiler.end('light');
  }

  // ------------------------------------------------------------- sampling

  /**
   * The 16×16 RGB tile grid for a chunk, or null when that chunk is unlit.
   *
   * The renderer's only entry point. Returned by reference and never copied:
   * one allocation per frame per chunk is the steady-state garbage the rest of
   * the pipeline goes out of its way not to produce.
   */
  tiles(cx: number, cy: number): Float32Array | null {
    const lc = this.map.get(chunkKey(cx, cy));
    return lc === undefined ? null : lc.light;
  }

  /** Light at a world cell, as luminance 0..1. */
  lightAt(x: number, y: number): number {
    const lc = this.map.get(chunkKey(x >> CHUNK_SHIFT, y >> CHUNK_SHIFT));
    if (lc === undefined) return 0;
    const t = (((y & CHUNK_MASK) >> LIGHT_SHIFT) << LIGHT_AXIS_SHIFT) |
      ((x & CHUNK_MASK) >> LIGHT_SHIFT);
    const i = t * 3;
    // Rec. 601 luma. The field is read for "how bright is it here", and a flat
    // channel mean would call saturated blue as bright as saturated yellow.
    return lc.light[i] * 0.299 + lc.light[i + 1] * 0.587 + lc.light[i + 2] * 0.114;
  }

  /** Light at a world cell, per channel, into `out`. */
  rgbAt(x: number, y: number, out: Float32Array): void {
    const lc = this.map.get(chunkKey(x >> CHUNK_SHIFT, y >> CHUNK_SHIFT));
    if (lc === undefined) {
      out[0] = 0;
      out[1] = 0;
      out[2] = 0;
      return;
    }
    const t = (((y & CHUNK_MASK) >> LIGHT_SHIFT) << LIGHT_AXIS_SHIFT) |
      ((x & CHUNK_MASK) >> LIGHT_SHIFT);
    const i = t * 3;
    out[0] = lc.light[i];
    out[1] = lc.light[i + 1];
    out[2] = lc.light[i + 2];
  }

  /** Drop every tile. For scene changes and tests. */
  clear(): void {
    this.map.clear();
    this.order.length = 0;
    this.orderDirty = false;
    this.gatherValid = false;
    this.relaxValid = false;
    this.admitQueue.length = 0;
    this.admitQueued.clear();
  }

  // ---------------------------------------------------------------- setup

  /**
   * Per-material emission colour, taken from the palette the renderer uses.
   *
   * Emissive matter lights the room in its own colour for free: lava is
   * red-orange because lava's palette entry is, not because anything here
   * knows what lava is. Rebuilt if the registry grows (test resets do this).
   */
  private ensureTables(): void {
    const n = materialCount();
    if (this.tablesFor === n) return;
    this.srcR = new Float32Array(n);
    this.srcG = new Float32Array(n);
    this.srcB = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      if (M.lightSource[i] === 0) continue;
      const e = M.emissive[i];
      const r = M.palette[i * 4] / 255;
      const g = M.palette[i * 4 + 1] / 255;
      const b = M.palette[i * 4 + 2] / 255;
      // Normalise to the brightest channel so a dim palette entry with high
      // emissive still reads as a light rather than as a grey smudge.
      const peak = r > g ? (r > b ? r : b) : g > b ? g : b;
      const k = peak > 0.001 ? e / peak : 0;
      this.srcR[i] = r * k;
      this.srcG[i] = g * k;
      this.srcB[i] = b * k;
    }
    this.tablesFor = n;
  }

  // ------------------------------------------------------- chunk lifecycle

  private ensureChunk(cx: number, cy: number): LightChunk | null {
    const key = chunkKey(cx, cy);
    const existing = this.map.get(key);
    if (existing !== undefined) return existing;
    const c = this.world.peekChunk(cx, cy);
    // COLD and FROZEN chunks are off-screen by two chunk rings or more. Light
    // there is invisible, and creating tiles for it is how a bounded field
    // turns into an unbounded one.
    if (c === null || c.tier > Tier.WARM) return null;
    const lc = new LightChunk(c);
    this.map.set(key, lc);
    this.orderDirty = true;
    return lc;
  }

  /**
   * Fold this tick's cell changes into pending gather rects, and admit chunks
   * that have become light sources.
   *
   * The admission test is `lightSources || burning`, both counters the world
   * already maintains incrementally. Scanning for emitters instead would mean
   * reading every cell of every visible chunk every tick, which is the cost the
   * dirty-rect scheduler exists to avoid.
   */
  private sync(): void {
    const active = this.world.activeChunks;
    for (let i = 0; i < active.length; i++) {
      const c = active[i];
      if (c.tier > Tier.WARM) continue;
      const key = chunkKey(c.cx, c.cy);
      const lc = this.map.get(key);
      if (lc === undefined) {
        if (c.lightSources === 0 && c.burning === 0) continue;
        this.queueAdmit(c.cx, c.cy);
        continue;
      }
      if (lc.chunk !== c) {
        // The chunk was evicted and regenerated under us. Every tile is stale.
        lc.chunk = c;
        lc.fullPending();
        continue;
      }
      // The movement rect covers matter appearing, moving or being destroyed;
      // the thermal rect covers ignition, burnout and phase change. Between
      // them they are every way a cell's emission or opacity can change, which
      // is why the field needs no dirty structure of its own.
      if (c.dirtyMaxX >= c.dirtyMinX) {
        lc.addCellRect(c.dirtyMinX, c.dirtyMinY, c.dirtyMaxX, c.dirtyMaxY);
      }
      if (c.thermMaxX >= c.thermMinX) {
        lc.addCellRect(c.thermMinX, c.thermMinY, c.thermMaxX, c.thermMaxY);
      }
    }
    if (this.orderDirty) this.rebuildOrder();
  }

  // ----------------------------------------------------------- admission

  /**
   * Note a chunk the field wants to take on. Cheap, unbounded, and does no work.
   *
   * Deduplicated against both the lit set and the queue, so a source that keeps
   * re-offering itself every tick — which is every burning chunk — cannot grow
   * the queue past the resident chunk count.
   */
  private queueAdmit(cx: number, cy: number): void {
    const key = chunkKey(cx, cy);
    if (this.map.has(key) || this.admitQueued.has(key)) return;
    this.admitQueued.add(key);
    this.admitQueue.push(cx, cy);
  }

  /**
   * Take on at most `maxAdmitPerTick` queued chunks, oldest first.
   *
   * Two bounds, not one. `maxAdmitPerTick` caps the admissions; a separate cap
   * of four times that caps the *attempts*, because a queued chunk can have been
   * evicted or tiered out since it was queued and a dead entry has to be able to
   * leave the queue. With only the admission cap, a camera cut that invalidated
   * fifty queued chunks spent every following tick's whole quota discarding them
   * and admitted nothing for seven ticks — the spike traded for a stall. A dead
   * entry costs one map lookup, so four per admission is a bounded price.
   */
  private drainAdmissions(): void {
    const q = this.admitQueue;
    if (q.length === 0) return;

    const maxAttempts = this.maxAdmitPerTick * 4;
    let head = 0;
    for (
      let n = 0;
      n < maxAttempts && this.admitsThisTick < this.maxAdmitPerTick && head < q.length;
      n++
    ) {
      const cx = q[head];
      const cy = q[head + 1];
      head += 2;
      this.admitQueued.delete(chunkKey(cx, cy));
      const lc = this.ensureChunk(cx, cy);
      // Out of range or evicted since it was queued. Dropped rather than
      // retried: whatever put it in the queue will put it back if it is still
      // true, and a queue that retains unreachable chunks never drains.
      if (lc === null) continue;
      // A chunk light is entering has never been read, so every tile of it is
      // stale — including its opacity, which is what decides whether the light
      // gets in at all.
      lc.fullPending();
      this.admitsThisTick++;
      this.workThisTick += ADMIT_COST;
    }

    // `copyWithin` rather than `splice`: `splice` returns the removed slice, and
    // a per-tick throwaway array is exactly the steady-state garbage the rest of
    // this file goes out of its way not to produce.
    if (head >= q.length) q.length = 0;
    else if (head > 0) {
      q.copyWithin(0, head);
      q.length -= head;
    }
  }

  private rebuildOrder(): void {
    this.order.length = 0;
    for (const lc of this.map.values()) this.order.push(lc);
    this.order.sort((a, b) => (a.cy - b.cy) || (a.cx - b.cx));
    this.orderDirty = false;
  }

  // --------------------------------------------------------------- gather

  private runGather(): void {
    const order = this.order;
    if (order.length === 0) {
      this.gatherValid = false;
      return;
    }

    let start = 0;
    let resumeTy = -1;
    if (this.gatherValid) {
      for (let i = 0; i < order.length; i++) {
        if (order[i].cx === this.gatherCx && order[i].cy === this.gatherCy) {
          start = i;
          resumeTy = this.gatherTy;
          break;
        }
      }
      this.gatherValid = false;
    }

    // Net of what admission already spent, so the tick as a whole still fits
    // one quota rather than three that happen to be added together.
    let budget = Math.floor(this.maxWorkPerTick * GATHER_SHARE) - this.workThisTick;

    for (let n = 0; n < order.length; n++) {
      const idx = start + n < order.length ? start + n : start + n - order.length;
      const lc = order[idx];
      if (!lc.pending) continue;

      const tx0 = lc.pMinTx;
      const tx1 = lc.pMaxTx;
      let ty0 = lc.pMinTy;
      const ty1Max = lc.pMaxTy;
      if (n === 0 && resumeTy > ty0) ty0 = resumeTy > ty1Max ? ty1Max : resumeTy;

      // Cost of one tile row: its tiles' worth of cells. Always at least one
      // row, so a chunk wider than the remaining quota still advances instead
      // of deadlocking — the same guarantee thermal's row loop makes.
      const rowCost = (tx1 - tx0 + 1) * CELLS_PER_TILE;
      let rows = (budget / rowCost) | 0;
      if (rows < 1) rows = 1;
      let ty1 = ty0 + rows - 1;
      const parked = ty1 < ty1Max;
      if (!parked) ty1 = ty1Max;

      // `gather` charges itself, from the band it is actually handed. Pricing it
      // here instead would repeat `thermal.ts`'s pre-fix mistake: an inner loop
      // that scans more than the rect the arithmetic priced reports a quota
      // being honoured while the system runs at several times its budget.
      this.gather(lc, tx0, tx1, ty0, ty1);
      lc.settled = false;
      budget -= (ty1 - ty0 + 1) * rowCost;

      if (parked) {
        this.gatherCx = lc.cx;
        this.gatherCy = lc.cy;
        this.gatherTy = ty1 + 1;
        this.gatherValid = true;
        return;
      }
      lc.clearPending();
      if (budget <= 0) {
        const nx = idx + 1 < order.length ? idx + 1 : 0;
        this.gatherCx = order[nx].cx;
        this.gatherCy = order[nx].cy;
        this.gatherTy = -1;
        this.gatherValid = true;
        return;
      }
    }
  }

  /** Reduce a band of tiles from cells: summed emission, mean opacity. */
  private gather(lc: LightChunk, tx0: number, tx1: number, ty0: number, ty1: number): void {
    this.workThisTick += (tx1 - tx0 + 1) * (ty1 - ty0 + 1) * CELLS_PER_TILE;
    const mat = lc.chunk.mat;
    const flags = lc.chunk.flags;
    const emit = lc.emit;
    const transmit = lc.transmit;
    const opacity = M.opacity;
    const srcR = this.srcR;
    const srcG = this.srcG;
    const srcB = this.srcB;
    const isSource = M.lightSource;

    for (let ty = ty0; ty <= ty1; ty++) {
      const cy0 = ty << LIGHT_SHIFT;
      for (let tx = tx0; tx <= tx1; tx++) {
        const cx0 = tx << LIGHT_SHIFT;
        let r = 0;
        let g = 0;
        let b = 0;
        let op = 0;
        for (let dy = 0; dy < LIGHT_TILE; dy++) {
          const row = (cy0 + dy) << CHUNK_SHIFT;
          for (let dx = 0; dx < LIGHT_TILE; dx++) {
            const i = row | (cx0 + dx);
            const m = mat[i];
            op += opacity[m];
            if (isSource[m] === 1) {
              r += srcR[m];
              g += srcG[m];
              b += srcB[m];
            }
            const f = flags[i];
            if (f & Flag.BURNING) {
              // Burning wood emits light even though wood's own `emissive` is
              // zero — the flame is the source, not the fuel.
              r += FIRE_R * BURN_EMIT;
              g += FIRE_G * BURN_EMIT;
              b += FIRE_B * BURN_EMIT;
            }
            if (f & Flag.LIT) {
              r += LIT_EMIT;
              g += LIT_EMIT;
              b += LIT_EMIT;
            }
          }
        }

        const t = (ty << LIGHT_AXIS_SHIFT) | tx;
        const e = t * 3;
        r *= PER_CELL_GAIN;
        g *= PER_CELL_GAIN;
        b *= PER_CELL_GAIN;
        emit[e] = r > 1 ? 1 : r;
        emit[e + 1] = g > 1 ? 1 : g;
        emit[e + 2] = b > 1 ? 1 : b;
        transmit[t] = 1 - (OCCLUDE * op) / CELLS_PER_TILE;
      }
    }
  }

  // ---------------------------------------------------------------- relax

  private runRelax(): void {
    const order = this.order;
    if (order.length === 0) {
      this.relaxValid = false;
      return;
    }

    let start = 0;
    if (this.relaxValid) {
      for (let i = 0; i < order.length; i++) {
        if (order[i].cx === this.relaxCx && order[i].cy === this.relaxCy) {
          start = i;
          break;
        }
      }
      this.relaxValid = false;
    }

    let budget = this.maxWorkPerTick - this.workThisTick;
    const reverse = (this.world.tick & 1) === 1;
    const perChunk = LIGHT_TILES * RELAX_COST;
    this.retire.length = 0;

    for (let n = 0; n < order.length; n++) {
      const idx = start + n < order.length ? start + n : start + n - order.length;
      const lc = order[idx];

      if (this.world.peekChunk(lc.cx, lc.cy) !== lc.chunk || lc.chunk.tier > Tier.WARM) {
        this.retire.push(lc);
        continue;
      }

      // A chunk with no source and no light left is not going to acquire
      // either without something touching it, and something touching it
      // re-admits it through `sync` or through a neighbour's spill.
      //
      // The test has to come *after* a relax, never before: `hasEmit` and
      // `maxLight` are what the relax computes, so a freshly admitted chunk
      // that has not been relaxed yet reads as empty and dark. Retiring on that
      // deletes every chunk light was about to spill into, on the tick before
      // it arrives.
      if (lc.settled) {
        if (this.trackDark(lc)) this.retire.push(lc);
        continue;
      }

      // As with gather: `relaxChunk` charges itself from its own loop bounds.
      if (this.relaxChunk(lc, reverse)) this.unsettleNeighbours(lc);
      else lc.settled = true;
      budget -= perChunk;

      if (this.trackDark(lc)) this.retire.push(lc);
      else this.spill(lc);

      if (budget <= 0) {
        const nx = idx + 1 < order.length ? idx + 1 : 0;
        this.relaxCx = order[nx].cx;
        this.relaxCy = order[nx].cy;
        this.relaxValid = true;
        break;
      }
    }

    for (let i = 0; i < this.retire.length; i++) {
      this.map.delete(chunkKey(this.retire[i].cx, this.retire[i].cy));
      this.orderDirty = true;
    }
    this.retire.length = 0;
    if (this.orderDirty) this.rebuildOrder();
  }

  /**
   * One propagation ply over a chunk's tiles.
   *
   * The interior/edge split is the one thermal's conduction loop makes and for
   * the same reason: 196 of 256 tiles have all four neighbours in this chunk's
   * own array, and paying a bounds test and a map lookup for them costs more
   * than the propagation itself.
   *
   * Returns true if any tile moved by more than `EPS` — the signal that this
   * chunk and its neighbours have not converged yet.
   */
  private relaxChunk(lc: LightChunk, reverse: boolean): boolean {
    this.workThisTick += LIGHT_PER_AXIS * LIGHT_PER_AXIS * RELAX_COST;
    const light = lc.light;
    const emit = lc.emit;
    const transmit = lc.transmit;
    const N = LIGHT_PER_AXIS;
    const stride = N * 3;
    let hasEmit = false;
    let maxL = 0;
    let changed = false;

    for (let i = 0; i < N; i++) {
      const ty = reverse ? N - 1 - i : i;
      const edgeRow = ty === 0 || ty === TILE_MASK;
      for (let j = 0; j < N; j++) {
        const tx = reverse ? N - 1 - j : j;
        const t = (ty << LIGHT_AXIS_SHIFT) | tx;
        const o = t * 3;

        let r: number;
        let g: number;
        let b: number;
        if (!edgeRow && tx > 0 && tx < TILE_MASK) {
          const up = o - stride;
          const dn = o + stride;
          const le = o - 3;
          const ri = o + 3;
          r = light[up];
          if (light[dn] > r) r = light[dn];
          if (light[le] > r) r = light[le];
          if (light[ri] > r) r = light[ri];
          g = light[up + 1];
          if (light[dn + 1] > g) g = light[dn + 1];
          if (light[le + 1] > g) g = light[le + 1];
          if (light[ri + 1] > g) g = light[ri + 1];
          b = light[up + 2];
          if (light[dn + 2] > b) b = light[dn + 2];
          if (light[le + 2] > b) b = light[le + 2];
          if (light[ri + 2] > b) b = light[ri + 2];
        } else {
          const acc = this.nbAcc;
          acc[0] = 0;
          acc[1] = 0;
          acc[2] = 0;
          this.accumNeighbour(lc, tx - 1, ty, acc);
          this.accumNeighbour(lc, tx + 1, ty, acc);
          this.accumNeighbour(lc, tx, ty - 1, acc);
          this.accumNeighbour(lc, tx, ty + 1, acc);
          r = acc[0];
          g = acc[1];
          b = acc[2];
        }

        const k = FALLOFF * transmit[t];
        r *= k;
        g *= k;
        b *= k;

        const er = emit[o];
        const eg = emit[o + 1];
        const eb = emit[o + 2];
        if (er > EPS || eg > EPS || eb > EPS) hasEmit = true;
        if (er > r) r = er;
        if (eg > g) g = eg;
        if (eb > b) b = eb;

        // Snap the tail to zero. Max-propagation decays geometrically and
        // never reaches zero on its own, so without this a burnt-out fire
        // leaves a permanent denormal glow and the chunk never retires.
        const nr = r > EPS ? r : 0;
        const ng = g > EPS ? g : 0;
        const nb = b > EPS ? b : 0;
        if (
          nr - light[o] > EPS || light[o] - nr > EPS ||
          ng - light[o + 1] > EPS || light[o + 1] - ng > EPS ||
          nb - light[o + 2] > EPS || light[o + 2] - nb > EPS
        ) {
          changed = true;
        }
        light[o] = nr;
        light[o + 1] = ng;
        light[o + 2] = nb;
        if (nr > maxL) maxL = nr;
        if (ng > maxL) maxL = ng;
        if (nb > maxL) maxL = nb;
      }
    }

    lc.hasEmit = hasEmit;
    lc.maxLight = maxL;
    return changed;
  }

  /** Advance the retirement counter. True once the chunk has been dark long enough. */
  private trackDark(lc: LightChunk): boolean {
    if (lc.hasEmit || lc.maxLight >= EPS || lc.pending) {
      lc.darkTicks = 0;
      return false;
    }
    return ++lc.darkTicks >= RETIRE_AFTER;
  }

  /**
   * A chunk whose light moved may now be feeding its neighbours differently,
   * so they have to be re-examined even if they had converged.
   */
  private unsettleNeighbours(lc: LightChunk): void {
    const n0 = this.map.get(chunkKey(lc.cx - 1, lc.cy));
    if (n0 !== undefined) n0.settled = false;
    const n1 = this.map.get(chunkKey(lc.cx + 1, lc.cy));
    if (n1 !== undefined) n1.settled = false;
    const n2 = this.map.get(chunkKey(lc.cx, lc.cy - 1));
    if (n2 !== undefined) n2.settled = false;
    const n3 = this.map.get(chunkKey(lc.cx, lc.cy + 1));
    if (n3 !== undefined) n3.settled = false;
  }

  private nbAcc = new Float32Array(3);

  private accumNeighbour(lc: LightChunk, tx: number, ty: number, out: Float32Array): void {
    let arr: Float32Array;
    let base: number;
    if (tx >= 0 && tx <= TILE_MASK && ty >= 0 && ty <= TILE_MASK) {
      arr = lc.light;
      base = ((ty << LIGHT_AXIS_SHIFT) | tx) * 3;
    } else {
      const ncx = lc.cx + (tx < 0 ? -1 : tx > TILE_MASK ? 1 : 0);
      const ncy = lc.cy + (ty < 0 ? -1 : ty > TILE_MASK ? 1 : 0);
      const n = this.map.get(chunkKey(ncx, ncy));
      if (n === undefined) return;
      arr = n.light;
      base = (((ty & TILE_MASK) << LIGHT_AXIS_SHIFT) | (tx & TILE_MASK)) * 3;
    }
    if (arr[base] > out[0]) out[0] = arr[base];
    if (arr[base + 1] > out[1]) out[1] = arr[base + 1];
    if (arr[base + 2] > out[2]) out[2] = arr[base + 2];
  }

  /**
   * Admit the neighbours a chunk's light is about to cross into.
   *
   * Only when there is light on that border, never unconditionally: dilating
   * the lit set by a ring every tick would create dark chunks that retire on
   * the next tick and are created again on the one after, which is a steady
   * 16384-cell gather per chunk per two ticks for no light at all.
   */
  private spill(lc: LightChunk): void {
    const light = lc.light;
    const N = LIGHT_PER_AXIS;
    const stride = N * 3;
    let left = 0;
    let right = 0;
    let top = 0;
    let bottom = 0;
    for (let t = 0; t < N; t++) {
      const rowBase = t * stride;
      for (let ch = 0; ch < 3; ch++) {
        const l = light[rowBase + ch];
        if (l > left) left = l;
        const r = light[rowBase + (N - 1) * 3 + ch];
        if (r > right) right = r;
        const u = light[t * 3 + ch];
        if (u > top) top = u;
        const d = light[(N - 1) * stride + t * 3 + ch];
        if (d > bottom) bottom = d;
      }
    }
    // Queued, not admitted: spill is the other half of the admission path and
    // has to answer to the same quota, or a camera cut that fires `sync` and a
    // spreading fire that fires `spill` would each be bounded and the pair of
    // them would not be.
    if (left > EPS) this.queueAdmit(lc.cx - 1, lc.cy);
    if (right > EPS) this.queueAdmit(lc.cx + 1, lc.cy);
    if (top > EPS) this.queueAdmit(lc.cx, lc.cy - 1);
    if (bottom > EPS) this.queueAdmit(lc.cx, lc.cy + 1);
  }
}
